// Attribution (W9c): who wrote which words, read from the document's own history. Every character of an Automerge
// text is an insert op named `counter@actor`, and `getCursor` hands back exactly that id for the character at an
// offset, so the actor that typed a character is a lookup; `members` (members.ts) turns the actor into a person.
//
// - `newRuns`: which characters of the text at `to` were written after `from` (the diff between the two heads,
//   replayed over the old text's length).
// - `wordsAddedBy`: History's "Anna added 312 words": the words of the text at `to` with letters written since `from`,
//   each put down to whoever wrote most of its new letters. A word counts once, by the same rule as `TextDiff`'s words
//   (letters or digits between spaces); markup alone isn't a word. A sentence cut and pasted elsewhere is new text
//   here (History's diff calls it moved).
// - `insertedBy`: ranges of the current text split by the actor that wrote them (revision mode's By author view).
// - `wordsByPerson`: the word log's devices (`"<day> <device> +|-"`) put down to members (Statistics).
// Offsets are UTF-16, as the editor and the JavaScript text count.
import * as A from '@automerge/automerge';
import { ROOT, SwiftReader, type ObjID } from '../automerge/swiftDoc.ts';
import type { RevisionChange } from '../revise/revisions.ts';
import type { Range16 } from '../text/scalars.ts';
import { actorOf, memberForDevice, UNKNOWN_COLOR, UNKNOWN_DEVICE, type Member, type Members } from './members.ts';

/** The fragment's `text` object at `heads` (else now), or null. */
function textObject(doc: A.Doc<unknown>, heads?: string[]): ObjID | null {
  const v = new SwiftReader(doc, heads).get(ROOT, 'text');
  return v && v[0] === 'text' ? (v[1] as ObjID) : null;
}

/** The fragment's text at `heads` (else now). */
export function textAt(doc: A.Doc<unknown>, heads?: string[]): string {
  const obj = textObject(doc, heads);
  return obj ? A.getBackend(doc).text(obj, heads) : '';
}

/**
 * The ranges of the text at `to` (default: now) written after `from`: everything when `from` is null (the fragment
 * didn't exist then) or the text object itself was replaced since. Sorted, never touching.
 */
export function newRuns(doc: A.Doc<unknown>, from: string[] | null, to?: string[]): Range16[] {
  const toHeads = to ?? A.getHeads(doc);
  const now = textObject(doc, toHeads);
  if (!now) return [];
  const length = A.getBackend(doc).text(now, toHeads).length;
  if (!from || textObject(doc, from) !== now) return length ? [[0, length]] : [];
  // Segments of the evolving text: old (false) or written since (true).
  const segments: { len: number; added: boolean }[] = [];
  const start = A.getBackend(doc).text(now, from).length;
  if (start) segments.push({ len: start, added: false });
  const splitAt = (index: number): number => {
    let at = 0;
    for (let i = 0; i < segments.length; i++) {
      const s = segments[i];
      if (index === at) return i;
      if (index < at + s.len) {
        segments.splice(i, 1, { len: index - at, added: s.added }, { len: at + s.len - index, added: s.added });
        return i + 1;
      }
      at += s.len;
    }
    return segments.length;
  };
  for (const p of A.diff(doc, from, toHeads)) {
    if (p.path.length !== 2 || p.path[0] !== 'text' || typeof p.path[1] !== 'number') continue;
    const index = p.path[1];
    if (p.action === 'splice') {
      if (!p.value.length) continue;
      segments.splice(splitAt(index), 0, { len: p.value.length, added: true });
    } else if (p.action === 'del') {
      const n = p.length ?? 1;
      const i = splitAt(index);
      const j = splitAt(index + n);
      segments.splice(i, j - i);
    }
  }
  const out: Range16[] = [];
  let at = 0;
  for (const s of segments) {
    if (s.added && s.len > 0) {
      const last = out[out.length - 1];
      if (last && last[1] === at) last[1] = at + s.len;
      else out.push([at, at + s.len]);
    }
    at += s.len;
  }
  return out;
}

/** A range of text and the actor whose op inserted it. */
export interface ActorRun { range: Range16; actor: string }

/** `ranges` of the text at `heads` (default: now) split by the actor that wrote each character. */
export function insertedBy(doc: A.Doc<unknown>, ranges: readonly Range16[], heads?: string[]): ActorRun[] {
  const obj = textObject(doc, heads);
  if (!obj) return [];
  const backend = A.getBackend(doc);
  const length = backend.text(obj, heads).length;
  const out: ActorRun[] = [];
  for (const [from, to] of ranges) {
    const end = Math.min(to, length);
    for (let i = Math.max(0, from); i < end; i++) {
      let actor: string;
      try {
        actor = actorOf(backend.getCursor(obj, i, heads));
      } catch {
        continue;
      }
      const last = out[out.length - 1];
      if (last && last.actor === actor && last.range[1] === i) last.range[1] = i + 1;
      else out.push({ range: [i, i + 1], actor });
    }
  }
  return out;
}

const WORDISH = /[\p{L}\p{N}]/u;
const SPACE = /\p{White_Space}/u;

/**
 * Words written between `from` (null: the fragment didn't exist) and `to` (default: now), by actor: each word of the
 * text at `to` with new letters or digits, put down to the actor who wrote most of them (the first, on a tie).
 */
export function wordsAddedBy(doc: A.Doc<unknown>, from: string[] | null, to?: string[]): Map<string, number> {
  const out = new Map<string, number>();
  const toHeads = to ?? A.getHeads(doc);
  const runs = newRuns(doc, from, toHeads);
  if (!runs.length) return out;
  const text = textAt(doc, toHeads);
  // Who wrote each new letter (only letters and digits are asked about: spaces and markup never decide).
  const letters = runs.map(([a, b]) => {
    const keep: Range16[] = [];
    for (let i = a; i < b; i++) {
      if (!WORDISH.test(text[i]) && !(i + 1 < b && WORDISH.test(text.slice(i, i + 2)))) continue;
      const last = keep[keep.length - 1];
      if (last && last[1] === i) last[1] = i + 1;
      else keep.push([i, i + 1]);
    }
    return keep;
  }).flat();
  const writers = insertedBy(doc, letters, toHeads);
  let k = 0;
  // Walk the words of the text: [start, end) between white space.
  let i = 0;
  const n = text.length;
  while (i < n && k < writers.length) {
    while (i < n && SPACE.test(text[i])) i++;
    const start = i;
    while (i < n && !SPACE.test(text[i])) i++;
    const end = i;
    if (end <= start) break;
    while (k < writers.length && writers[k].range[1] <= start) k++;
    const tally = new Map<string, number>();
    for (let j = k; j < writers.length && writers[j].range[0] < end; j++) {
      const r = writers[j];
      const overlap = Math.min(end, r.range[1]) - Math.max(start, r.range[0]);
      if (overlap > 0) tally.set(r.actor, (tally.get(r.actor) ?? 0) + overlap);
    }
    if (!tally.size || !WORDISH.test(text.slice(start, end))) continue;
    let best = '', most = -1;
    for (const [actor, count] of tally) if (count > most) { best = actor; most = count; }
    out.set(best, (out.get(best) ?? 0) + 1);
  }
  return out;
}

// MARK: People

/** A person's share of something (a member, or the devices no member has claimed: `id` null). */
export interface PersonFigure { id: string | null; name: string; color: string }

export const unknownPerson = (): PersonFigure => ({ id: null, name: UNKNOWN_DEVICE, color: UNKNOWN_COLOR });
const personOf = (m: Member | null | undefined): PersonFigure => (m ? { id: m.id, name: m.name, color: m.color || UNKNOWN_COLOR } : unknownPerson());

/** Counts by actor folded into counts by person, members in the members' order, then Unknown device. */
export function byPerson(members: Members, byActor: Map<string, number>): (PersonFigure & { words: number })[] {
  const totals = new Map<string | null, number>();
  for (const [actor, n] of byActor) {
    const id = members.byActor.get(actor)?.id ?? null;
    totals.set(id, (totals.get(id) ?? 0) + n);
  }
  const out: (PersonFigure & { words: number })[] = [];
  for (const m of members.list) if (totals.get(m.id)) out.push({ ...personOf(m), words: totals.get(m.id)! });
  if (totals.get(null)) out.push({ ...unknownPerson(), words: totals.get(null)! });
  return out;
}

// MARK: Revision mode, by author

/** Revision changes recoloured by who wrote the added words: each `levels` part split by actor, its `level` the
 *  writer's index in `members.list` (unclaimed writing: `members.list.length`). Deletions take the change's first
 *  writer, else the unclaimed index. `doc` must hold the text the changes were worked out from. */
export function revisionChangesByAuthor(changes: readonly RevisionChange[], doc: A.Doc<unknown>, members: Members): RevisionChange[] {
  const unknown = members.list.length;
  const index = new Map(members.list.map((m, i) => [m.id, i]));
  const levelOf = (actor: string) => {
    const m = members.byActor.get(actor);
    return m ? index.get(m.id)! : unknown;
  };
  return changes.map((c) => {
    const levels: { range: Range16; level: number }[] = [];
    for (const part of c.levels) {
      for (const run of insertedBy(doc, [part.range])) {
        const level = levelOf(run.actor);
        const last = levels[levels.length - 1];
        if (last && last.level === level && last.range[1] === run.range[0]) last.range = [last.range[0], run.range[1]];
        else levels.push({ range: [run.range[0], run.range[1]], level });
      }
    }
    return { ...c, levels, deletionLevel: levels[0]?.level ?? unknown };
  });
}

// MARK: Statistics, by person

export interface PersonWords extends PersonFigure {
  added: number;
  removed: number;
  /** Days with words written or cut (writing sessions, a day each, as the Mac keeps them). */
  days: number;
  /** The last of those days (yyyy-MM-dd), or null. */
  lastDay: string | null;
  /** Written and cut per day, oldest first. */
  daily: { day: string; added: number; removed: number }[];
}

/** The word logs of `logs` (each fragment's `wordLog`), split by the member each device belongs to. Members with no
 *  words are listed too (they've joined); devices no member claims are one "Unknown device" row, when they wrote. */
export function wordsByPerson(members: Members, logs: Iterable<Record<string, number>>): PersonWords[] {
  const rows = new Map<string | null, Map<string, { added: number; removed: number }>>();
  for (const log of logs) {
    for (const [key, amount] of Object.entries(log)) {
      const parts = key.split(' ').filter((p) => p !== '');
      if (parts.length !== 3 || !Number.isFinite(amount)) continue;
      const id = memberForDevice(members, parts[1])?.id ?? null;
      const days = rows.get(id) ?? new Map();
      const day = days.get(parts[0]) ?? { added: 0, removed: 0 };
      if (parts[2] === '+') day.added += amount; else day.removed += amount;
      days.set(parts[0], day);
      rows.set(id, days);
    }
  }
  const row = (p: PersonFigure, days: Map<string, { added: number; removed: number }> | undefined): PersonWords => {
    const daily = [...(days ?? [])].sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)).map(([day, w]) => ({ day, ...w }));
    const written = daily.filter((d) => d.added > 0 || d.removed > 0);
    return {
      ...p, daily, days: written.length, lastDay: written.at(-1)?.day ?? null,
      added: daily.reduce((n, d) => n + d.added, 0), removed: daily.reduce((n, d) => n + d.removed, 0),
    };
  };
  const out = members.list.map((m) => row(personOf(m), rows.get(m.id)));
  if (rows.has(null)) out.push(row(unknownPerson(), rows.get(null)));
  return out;
}

/** The word log's entries written by these devices only (the Library home's "this week" in a shared project: yours). */
export function logOfDevices(log: Record<string, number>, devices: ReadonlySet<string>): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [key, amount] of Object.entries(log)) {
    const parts = key.split(' ').filter((p) => p !== '');
    if (parts.length === 3 && devices.has(parts[1])) out[key] = amount;
  }
  return out;
}
